import React, { useMemo } from 'react';
import { Link } from 'react-router';
import { lscPath } from '../routing';
import type { LeagueSetupWizardResult } from '../leagueSetupWizard';
import { resolveOnboardingWizardStep } from '../activationWizardStep';
import type {
  LscLeague,
  LscLeagueReadiness,
  LscLeagueSetupProgress,
  LscLeagueSetupProgressStepId,
} from '../types';
import {
  ONBOARDING_STEP_ORDER,
  isOnboardingStepId,
  nextOnboardingStepId,
  onboardingStepDefinition,
  previousOnboardingStepId,
} from '../onboardingSteps';
import LeagueSetupStepCard from './LeagueSetupStepCard';
import LeagueBasicsStepPanel from './wizard/LeagueBasicsStepPanel';
import MembersTeamsStepPanel from './wizard/MembersTeamsStepPanel';
import ScheduleSetupStepPanel from './wizard/ScheduleSetupStepPanel';
import TwitchLiveStepPanel from './wizard/TwitchLiveStepPanel';
import AutomationActivationStepPanel from './wizard/AutomationActivationStepPanel';
import SubscriptionStepPanel from './wizard/SubscriptionStepPanel';
import LoadingState from './LoadingState';

interface LeagueOnboardingShellProps {
  leagueId: string;
  leagueName: string;
  league: LscLeague;
  readiness: LscLeagueReadiness;
  isOwner: boolean;
  isCommissioner: boolean;
  wizardResult: LeagueSetupWizardResult;
  setupProgress: LscLeagueSetupProgress | null;
  activeStepId: LscLeagueSetupProgressStepId;
  onStepChange: (stepId: LscLeagueSetupProgressStepId) => void;
  onLeagueUpdated: (league: LscLeague) => void;
  onSetupRefresh: () => void;
  progressLoading?: boolean;
  leagueArchived?: boolean;
}

function stepTone(status: string): string {
  if (status === 'complete' || status === 'optional' || status === 'not_applicable') {
    return 'border-emerald-700/40 bg-emerald-950/10 text-emerald-200';
  }
  if (status === 'warning') {
    return 'border-amber-700/40 bg-amber-950/10 text-amber-100';
  }
  if (status === 'locked') {
    return 'border-secondary-700 bg-secondary-900/30 text-secondary-400';
  }
  return 'border-secondary-700 bg-secondary-900/40 text-secondary-200';
}

const LeagueOnboardingShell: React.FC<LeagueOnboardingShellProps> = ({
  leagueId,
  leagueName,
  league,
  readiness,
  isOwner,
  isCommissioner,
  wizardResult,
  setupProgress,
  activeStepId,
  onStepChange,
  onLeagueUpdated,
  onSetupRefresh,
  progressLoading = false,
  leagueArchived = false,
}) => {
  const stepSummaries = setupProgress?.steps ?? [];
  const summaryById = useMemo(
    () => new Map(stepSummaries.map((step) => [step.step_id, step])),
    [stepSummaries]
  );

  const activeDefinition = onboardingStepDefinition(activeStepId);
  const activeWizardStep = resolveOnboardingWizardStep(
    wizardResult.steps,
    activeStepId,
    summaryById.get(activeStepId)
  );
  const previousStepId = previousOnboardingStepId(activeStepId);
  const nextStepId = nextOnboardingStepId(activeStepId);
  const commandCenterHref = lscPath(`/leagues/${leagueId}/command-center`);

  if (progressLoading && !setupProgress) {
    return <LoadingState message="Loading onboarding progress…" />;
  }

  return (
    <div className="space-y-6 overflow-x-hidden" data-testid="league-onboarding-shell">
      <section className="rounded-xl border border-secondary-700 bg-secondary-900/40 px-4 py-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-secondary-500">Production onboarding</p>
            <h2 className="text-lg font-semibold text-white mt-1">{leagueName}</h2>
            <p className="text-sm text-secondary-400 mt-1">
              Step {ONBOARDING_STEP_ORDER.findIndex((step) => step.id === activeStepId) + 1} of{' '}
              {ONBOARDING_STEP_ORDER.length}: {activeDefinition.label}
            </p>
          </div>
          {setupProgress?.is_setup_complete ? (
            <span className="text-xs font-medium text-emerald-300 border border-emerald-700/50 rounded-full px-3 py-1">
              Setup complete
            </span>
          ) : (
            <span className="text-xs font-medium text-amber-200 border border-amber-700/50 rounded-full px-3 py-1">
              Resume anytime
            </span>
          )}
        </div>
      </section>

      <nav
        aria-label="Onboarding steps"
        className="-mx-1 overflow-x-auto pb-1 scroll-smooth snap-x snap-mandatory"
        data-testid="onboarding-step-nav"
      >
        <div className="flex min-w-max gap-2 px-1 sm:grid sm:min-w-0 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
        {ONBOARDING_STEP_ORDER.map((step) => {
          const summary = summaryById.get(step.id);
          const isActive = step.id === activeStepId;
          const status = summary?.status ?? 'blocked';
          return (
            <button
              key={step.id}
              type="button"
              onClick={() => onStepChange(step.id)}
              className={`min-h-11 min-w-[9.5rem] shrink-0 snap-start rounded-lg border px-3 py-2 text-left transition sm:min-w-0 ${stepTone(status)} ${
                isActive ? 'ring-2 ring-primary-500/60' : 'hover:border-secondary-500'
              }`}
              data-testid={`onboarding-step-${step.id}`}
              data-active={isActive ? 'true' : 'false'}
            >
              <p className="text-[11px] uppercase tracking-wide opacity-80">{step.shortLabel}</p>
              <p className="text-sm font-medium mt-0.5">{step.label}</p>
            </button>
          );
        })}
        </div>
      </nav>

      <section className="space-y-4" data-testid="onboarding-active-step">
        <div>
          <h3 className="text-xl font-semibold text-white">{activeDefinition.label}</h3>
          <p className="text-sm text-secondary-400 mt-1">{activeDefinition.description}</p>
        </div>

        {activeStepId === 'subscription' && activeWizardStep ? (
          <SubscriptionStepPanel
            leagueId={leagueId}
            wizardStep={activeWizardStep}
            isOwner={isOwner}
            isOperator={isOwner || isCommissioner}
            leagueArchived={leagueArchived}
            onSetupRefresh={onSetupRefresh}
          />
        ) : activeStepId === 'automation_activation' && activeWizardStep ? (
          <AutomationActivationStepPanel
            leagueId={leagueId}
            wizardStep={activeWizardStep}
            isOwner={isOwner}
            isOperator={isOwner || isCommissioner}
            onSetupRefresh={onSetupRefresh}
          />
        ) : activeStepId === 'league_basics' && activeWizardStep ? (
          <LeagueBasicsStepPanel
            league={league}
            wizardStep={activeWizardStep}
            isOwner={isOwner}
            isOperator={isOwner || isCommissioner}
            leagueArchived={leagueArchived}
            onLeagueUpdated={onLeagueUpdated}
            onSetupRefresh={onSetupRefresh}
          />
        ) : activeStepId === 'schedule_source' && activeWizardStep ? (
          <ScheduleSetupStepPanel
            leagueId={leagueId}
            league={league}
            wizardStep={activeWizardStep}
            readiness={readiness}
            leagueArchived={leagueArchived}
            onSetupRefresh={onSetupRefresh}
          />
        ) : activeStepId === 'members_teams' && activeWizardStep ? (
          <MembersTeamsStepPanel
            leagueId={leagueId}
            league={league}
            wizardStep={activeWizardStep}
            readiness={readiness}
            isCommissioner={isCommissioner}
            leagueArchived={leagueArchived}
            onSetupRefresh={onSetupRefresh}
          />
        ) : activeStepId === 'twitch_live' && activeWizardStep ? (
          <TwitchLiveStepPanel
            leagueId={leagueId}
            wizardStep={activeWizardStep}
            readiness={readiness}
            isOwner={isOwner}
            onSetupRefresh={onSetupRefresh}
          />
        ) : activeWizardStep ? (
          <LeagueSetupStepCard step={activeWizardStep} />
        ) : (
          <div className="card text-sm text-secondary-400">
            Step details are unavailable right now. Try again in a moment.
          </div>
        )}
      </section>

      <div
        className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="flex flex-col gap-2 sm:flex-row">
          {previousStepId ? (
            <button
              type="button"
              className="btn-secondary text-sm min-h-11 px-4"
              onClick={() => onStepChange(previousStepId)}
              data-testid="onboarding-previous-step"
            >
              Previous
            </button>
          ) : null}
          {nextStepId ? (
            <button
              type="button"
              className="btn-primary text-sm min-h-11 px-4"
              onClick={() => onStepChange(nextStepId)}
              data-testid="onboarding-next-step"
            >
              Next step
            </button>
          ) : null}
        </div>
        <Link
          to={commandCenterHref}
          className="inline-flex min-h-11 items-center text-sm text-secondary-400 hover:text-white"
        >
          Skip to Command Center
        </Link>
      </div>
    </div>
  );
};

export function resolveInitialOnboardingStep(
  queryStep: string | null,
  setupProgress: LscLeagueSetupProgress | null
): LscLeagueSetupProgressStepId {
  if (isOnboardingStepId(queryStep)) {
    return queryStep;
  }
  if (setupProgress?.resume_step_id && isOnboardingStepId(setupProgress.resume_step_id)) {
    return setupProgress.resume_step_id;
  }
  if (setupProgress?.current_step_id && isOnboardingStepId(setupProgress.current_step_id)) {
    return setupProgress.current_step_id;
  }
  return 'subscription';
}

export default LeagueOnboardingShell;
